import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ApiError } from '@/api/types';
import { importBridgeKey } from '@/crypto';
import { AppCard, FlowGuide, GuideSkeleton } from '@/components/AppCard';
import { BackButton } from '@/components/BackButton';
import { Button } from '@/components/Button';
import { ChatDetails } from '@/components/ChatDetails';
import { ChatView } from '@/components/ChatView';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmbeddedLink } from '@/components/EmbeddedLink';
import { Page } from '@/components/Layout';
import { MetaLine } from '@/components/MetaLine';
import { NewBridgeLink } from '@/components/NewBridgeLink';
import { OpenGate } from '@/components/OpenGate';
import { SealedSecret } from '@/components/SealedSecret';
import { ExpiredTitle } from '@/components/ExpiredFlies';
import { SecretItem } from '@/components/SecretView';
import { ShareOverlay } from '@/components/ShareOverlay';
import { SharePending } from '@/components/SharePending';
import { NO_VIEWS_LEFT, SharePopover } from '@/components/SharePopover';
import { useToast } from '@/components/Toast';
import { useMessageOrder } from '@/hooks/useMessageOrder';
import { usePreview } from '@/hooks/usePreview';
import ui from '@/components/ui.module.css';
import { platform } from '@platform';
import { isEmbedded } from '@/lib/embed';
import { ownerProgress, type FlowId } from '@/lib/flows';
import { bridgePath, keyFromHash } from '@/lib/links';
import { orderNotice } from '@/lib/order';
import { getBridgeKey, getClaimToken, getOwnerToken, getPurpose, setBridgeKey, setOwnerToken } from '@/lib/storage';
import { sealDeletionMark } from '@/services/deletion';
import { inTurn } from '@/services/outgoing';
import { useLocale, useT, type MessageKey } from '@/i18n';

import styles from './BridgePage.module.css';
import shared from './pages.module.css';
import { useBridge } from './useBridge';

interface StatusCopy {
  readonly title: MessageKey;
  readonly text: MessageKey;
}

/** How long the finished guide stays, all green, before the chat details take its place. */
const GUIDE_SWAP_MS = 1600;

const STATUS: Record<string, StatusCopy> = {
  // the server removes an expired bridge, so an old link lands here: it cannot tell it from one that never was, and says what is likely
  not_found: { title: 'bridgePage.notFoundTitle', text: 'bridgePage.notFoundText' },
  expired: { title: 'bridgePage.expiredTitle', text: 'bridgePage.expiredText' },
  view_limit: { title: 'bridgePage.viewLimitTitle', text: 'bridgePage.viewLimitText' },
  deleted: { title: 'bridgePage.deletedTitle', text: 'bridgePage.deletedText' },
  invalid_link: { title: 'bridgePage.invalidTitle', text: 'bridgePage.invalidText' },
  decrypt: { title: 'bridgePage.decryptTitle', text: 'bridgePage.decryptText' },
  rate_limited: { title: 'bridgePage.rateTitle', text: 'bridgePage.rateText' },
  network: { title: 'bridgePage.networkTitle', text: 'bridgePage.networkText' },
  internal: { title: 'bridgePage.internalTitle', text: 'bridgePage.internalText' },
};

/** A bridge on its way in: the outline of the screen, shimmering, so nothing jumps when it arrives. */
function BridgeSkeleton(): React.JSX.Element {
  const t = useT();
  return (
    <div className={styles.bridgeSkeleton} role="status" data-testid="bridge-skeleton">
      <span className="visually-hidden">{t('bridgePage.opening')}</span>
      <span className={styles.skeletonHead} aria-hidden="true">
        <span className={[shared.skeleton, shared.skeletonIcon].join(' ')} />
        <span className={shared.skeletonLines}>
          <span className={[shared.skeleton, shared.skeletonTitle].join(' ')} />
          <span className={[shared.skeleton, shared.skeletonLine].join(' ')} style={{ width: '72%' }} />
        </span>
      </span>
      <span className={[shared.skeleton, shared.skeletonBlock].join(' ')} aria-hidden="true" />
      <span className={[shared.skeleton, shared.skeletonLine].join(' ')} style={{ width: '46%' }} aria-hidden="true" />
    </div>
  );
}

function StatusCard({ code }: { readonly code: string }): React.JSX.Element {
  const t = useT();
  const copy = STATUS[code] ?? STATUS.internal ?? { title: 'bridgePage.internalTitle', text: 'bridgePage.internalText' };
  return (
    <AppCard className={shared.statusCard} role="alert" testId="status" status={code} stepKey={`status-${code}`}>
      <h2>{code === 'expired' || code === 'not_found' ? <ExpiredTitle>{t(copy.title)}</ExpiredTitle> : t(copy.title)}</h2>
      <p>{t(copy.text)}</p>
      <div className={shared.statusActions}>
        <NewBridgeLink>{t('bridgePage.createNew')}</NewBridgeLink>
      </div>
    </AppCard>
  );
}

export function BridgePage(): React.JSX.Element {
  const t = useT();
  const { code: language } = useLocale();
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const guestPreview = new URLSearchParams(location.search).get('as') === 'guest';
  const [key, setKey] = useState<CryptoKey | null>(null);
  // A visitor with no token yet opens the bridge (and spends a view) only by pressing the button, and only once the
  // key in the link has been checked: a link scanner that runs the page's script then burns nothing and reads nothing.
  const [pressed, setPressed] = useState<string | null>(null);
  // what the person pressed on the sealed card: Open secret (show the text) or Download (save the file)
  const [intent, setIntent] = useState<'reveal' | 'download' | null>(null);
  const holdsToken = (!guestPreview && getOwnerToken(id) !== null) || getClaimToken(id) !== null;
  const opened = holdsToken || pressed === id;
  // In another site's frame a bridge is only a link (see EmbeddedLink): nothing is opened and no view is spent here.
  const framed = isEmbedded();
  // Before the press the page asks only what the link may show (kind, expiry): that spends nothing and brings no ciphertext.
  const preview = usePreview(id, key !== null && !opened && !framed);
  const bridge = useBridge(id, guestPreview, key !== null && opened && !framed);
  // the list as the person should see it, and whether the server left a hole in it (docs/FORMAT.md §5)
  const order = useMessageOrder(bridge.view, key, id);
  const notice = orderNotice(order.problems);
  const [keyError, setKeyError] = useState(false);
  const arrival = location.state as { justCreated?: boolean; flow?: FlowId } | null;
  // Straight from creation: the share screen is already on screen, so this page takes over without an entrance.
  const [justCreated] = useState<boolean>(() => Boolean(arrival?.justCreated));
  const [createdFlow] = useState<FlowId>(() => arrival?.flow ?? 'secret');
  const [showShare, setShowShare] = useState<boolean>(justCreated);
  // the Mac shortcut opens a chat it has just made and copied with ?copied=1: the chat says so on a plate, and the mark leaves the address
  const [copied] = useState(() => new URLSearchParams(location.search).get('copied') === '1');
  useEffect(() => {
    if (!copied) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('copied');
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query === '' ? '' : `?${query}`}${window.location.hash}`);
  }, [copied]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  // the key is in the link; once this tab has opened it, it is in the tab's session and no longer in the address bar
  const encodedKey = keyFromHash(location.hash, location.search) ?? getBridgeKey(id);

  useEffect(() => {
    document.title = t('bridgePage.docTitle');
  }, [language, t]);

  useEffect(() => {
    if (encodedKey === null) return;
    let cancelled = false;
    importBridgeKey(encodedKey)
      .then((k) => {
        if (!cancelled) {
          setKey(k);
          setKeyError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKey(null);
          setKeyError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [encodedKey]);

  // The key has opened, so it moves from the address bar into this tab's session, and the address loses it: the owner's share
  // screen still shows the link until it is closed. A page in another site's frame only shows a link and keeps nothing.
  useEffect(() => {
    if (key === null || encodedKey === null || framed) return;
    setBridgeKey(id, encodedKey);
    if (!showShare) platform.hideKeyInAddress();
  }, [key, encodedKey, framed, id, showShare]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const isOwner = bridge.role === 'owner';
  const view = order.view;
  const mode = view?.mode ?? 'secret';
  const purpose = getPurpose(id) ?? 'chat';
  const messageCount = view?.messages.length ?? 0;
  const firstFileId = view?.messages.find((m) => m.kind === 'file')?.id;
  const guestArrived = view !== null && (view.messages.some((m) => m.from === 'guest') || view.views > 0);

  // A "receive" channel: the share screen gives way to the chat as soon as the other side shows up.
  useEffect(() => {
    if (showShare && mode === 'chat' && guestArrived) {
      const handle = window.setTimeout(() => {
        setShowShare(false);
      }, 1400);
      return () => {
        window.clearTimeout(handle);
      };
    }
    return undefined;
  }, [showShare, mode, guestArrived]);

  // The step guide beside the bridge: where the owner (or the recipient) is in the flow.
  const guestMessages = view?.messages.filter((m) => m.from === 'guest').length ?? 0;
  const flow: FlowId = isOwner ? (mode === 'secret' ? 'secret' : purpose) : mode === 'secret' ? 'guestSecret' : 'guestChat';
  const progress = isOwner
    ? ownerProgress(mode === 'secret' ? 'secret' : purpose, { views: view?.views ?? 0, guestMessages })
    : mode === 'secret'
      ? 2
      : (view?.messages.some((m) => m.from === 'guest') ?? false)
        ? 3
        : 2;
  const guide = <FlowGuide flow={flow} progress={progress} />;

  // When a chat has done its job — the other side has written — the steps are no longer news:
  // they all turn green, and a moment later the panel shows what is worth a glance while talking.
  // A chat that is already under way when the page opens starts that way.
  const chatDone = mode === 'chat' && guestMessages > 0;
  const [guideState, setGuideState] = useState<'pending' | 'steps' | 'details'>('pending');
  if (view !== null && guideState === 'pending') setGuideState(chatDone ? 'details' : 'steps');
  useEffect(() => {
    if (guideState !== 'steps' || !chatDone || showShare) return undefined;
    const handle = window.setTimeout(() => {
      setGuideState('details');
    }, GUIDE_SWAP_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [guideState, chatDone, showShare]);

  const removeMessage = async (messageId: string): Promise<void> => {
    try {
      // in turn with what is being sent: the mark takes the next number, which only works if nothing else has one in flight
      await inTurn(id, async () => {
        // a deletion mark first, so the hole the removal leaves is not taken for a message the server dropped
        const target = bridge.view?.messages.find((message) => message.id === messageId);
        const mark = target === undefined || key === null ? null : await sealDeletionMark(key, target, { bridgeId: id, from: bridge.role ?? 'owner' });
        try {
          if (mark !== null) await bridge.send(mark);
        } catch (cause) {
          // a bridge that is full has no room for the mark: the message goes first, and the mark takes the room that frees
          if (!(cause instanceof ApiError && cause.code === 'storage_full') || mark === null) throw cause;
          await bridge.remove(messageId);
          await bridge.send(mark);
          return;
        }
        await bridge.remove(messageId);
      });
    } catch {
      toast.error(t('bridgePage.deleteMessageFailed'));
    }
  };

  const destroy = async (): Promise<void> => {
    setDeleting(true);
    try {
      await bridge.destroy();
      setOwnerToken(id, null);
      setConfirmDelete(false);
      toast.notify(t('bridgePage.deleted'));
      void navigate('/');
    } catch {
      toast.error(t('bridgePage.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  // Share, once the bridge is up: a dropdown with Link, QR code, Code and Nearby.
  const share =
    isOwner && !guestPreview && bridge.token !== null && encodedKey !== null && view !== null
      ? (close: () => void): React.JSX.Element => (
          <ShareOverlay
            variant="popover"
            bridgeId={id}
            bridgeKey={encodedKey}
            ownerToken={bridge.token ?? ''}
            viewsLeft={view.maxViews - view.views}
            waiting={mode === 'chat'}
            purpose={purpose}
            connectRequest={bridge.connectRequest}
            onClose={close}
            onDeviceConnected={() => {
              window.setTimeout(close, 1500);
            }}
          />
        )
      : undefined;

  const ownerActions = isOwner ? (
    <div className={shared.metaActions}>
      {share ? (
        <SharePopover buttonClassName={[ui.btn, ui.secondary, ui.small, shared.shareMain].join(' ')} title={t('bridgePage.shareTitle')} blocked={view !== null && view.views >= view.maxViews ? NO_VIEWS_LEFT : null}>
          {share}
        </SharePopover>
      ) : null}
      <Button
        variant="danger"
        size="small"
        className={shared.ownerDelete}
        onClick={() => {
          setConfirmDelete(true);
        }}
        data-testid="delete-bridge"
      >
        {t('bridgePage.deleteBridge')}
      </Button>
    </div>
  ) : null;

  // The ← on every screen: the first screen, or — after a look as the recipient — the owner's own view.
  const backTo = guestPreview && encodedKey !== null ? bridgePath(id, encodedKey) : '/';

  // ------------------------------------------------------------ what to show
  let body: React.JSX.Element;
  if (encodedKey === null || keyError) {
    body = <StatusCard code="invalid_link" />;
  } else if (framed) {
    body = <EmbeddedLink bridgeId={id} bridgeKey={encodedKey} />;
  } else if (!opened && key !== null) {
    if (preview.status === 'error') {
      body = <StatusCard code={preview.error} />;
    } else if (preview.status !== 'ready') {
      body = (
        <AppCard guide={<GuideSkeleton />} stepKey="view" busy>
          <BridgeSkeleton />
        </AppCard>
      );
    } else if (preview.preview.mode === 'chat') {
      // joining a chat is the one click that takes the view
      body = (
        <OpenGate
          onOpen={() => {
            setPressed(id);
          }}
        />
      );
    } else {
      body = (
        <SealedSecret
          preview={preview.preview}
          now={now}
          guide={<FlowGuide flow="guestSecret" progress={1} />}
          onReveal={() => {
            setIntent('reveal');
            setPressed(id);
          }}
          onDownload={() => {
            setIntent('download');
            setPressed(id);
          }}
        />
      );
    }
  } else if (bridge.status === 'error') {
    body = <StatusCard code={bridge.error ?? 'internal'} />;
  } else if (bridge.gone !== null) {
    body = <StatusCard code={bridge.gone} />;
  } else if (bridge.status === 'loading' || key === null || view === null) {
    body = justCreated ? (
      <AppCard guide={<FlowGuide flow={createdFlow} progress={2} />} stepKey="share" enter={false} busy>
        <SharePending waiting={createdFlow !== 'secret'} purpose={createdFlow === 'receive' ? 'receive' : 'chat'} />
      </AppCard>
    ) : (
      <AppCard guide={<GuideSkeleton />} stepKey="view" busy>
        <BridgeSkeleton />
      </AppCard>
    );
  } else if (showShare && isOwner && !guestPreview && bridge.token !== null) {
    body = (
      <AppCard guide={guide} stepKey="share" enter={!justCreated}>
        <ShareOverlay
          bridgeId={id}
          bridgeKey={encodedKey}
          ownerToken={bridge.token}
          viewsLeft={view.maxViews - view.views}
          waiting={mode === 'chat' && messageCount === 0}
          purpose={purpose}
          connectRequest={bridge.connectRequest}
          onClose={() => {
            setShowShare(false);
          }}
          onDeviceConnected={() => {
            window.setTimeout(() => {
              setShowShare(false);
            }, 1500);
          }}
        />
      </AppCard>
    );
  } else if (mode === 'secret') {
    const viewsLeft = view.maxViews - view.views;
    body = (
      <AppCard guide={guide} stepKey="view" direction="back" testId="bridge-card" mode="secret" enter={!justCreated}>
        <div className={[styles.secretHead, isOwner || guestPreview ? '' : styles.secretHeadSolo].join(' ')}>
          {isOwner || guestPreview ? (
            <span className={styles.secretBack}>
              <BackButton to={backTo} label={guestPreview ? t('bridgePage.backToBridge') : t('bridgePage.backToStart')} />
            </span>
          ) : null}
          <h2 className={styles.secretTitle}>{isOwner ? t('bridgePage.ownerTitle') : t('bridgePage.guestTitle')}</h2>
          <p className={styles.secretSub}>
            {isOwner
              ? t('bridgePage.ownerSub')
              : viewsLeft <= 0
                ? t('bridgePage.guestSubLast')
                : t('bridgePage.guestSub')}
          </p>
        </div>
        {notice === null ? null : (
          <p className={[shared.notice, shared.noticeWarn].join(' ')} role="alert" data-testid="order-notice">
            {notice}
          </p>
        )}
        <div className={styles.secretList}>
          {messageCount === 0 ? <p className={styles.empty}>{t('bridgePage.empty')}</p> : null}
          {view.messages.map((message) => (
            <SecretItem
              key={message.id}
              message={message}
              bridgeId={id}
              bridgeKey={key}
              fetchChunk={bridge.fetchChunk}
              sole={messageCount === 1}
              canDelete={isOwner}
              onDelete={(mid) => void removeMessage(mid)}
              startRevealed={intent === 'reveal'}
              autoDownload={intent === 'download' && message.id === firstFileId}
            />
          ))}
        </div>
        {viewsLeft <= 0 && !isOwner ? <p className={shared.notice}>{t('bridgePage.lastViewGuest')}</p> : null}
        {viewsLeft <= 0 && isOwner ? <p className={shared.notice}>{t('bridgePage.lastViewOwner')}</p> : null}
        <div className={[shared.secretFoot, isOwner ? shared.secretFootOwner : ''].join(' ')}>
          <MetaLine view={view} online={bridge.online} now={now} />
          {ownerActions}
          <NewBridgeLink own={!isOwner} />
        </div>
      </AppCard>
    );
  } else {
    body = (
      <AppCard
        guide={guideState === 'details' ? <ChatDetails view={view} now={now} purpose={purpose} presence={bridge.presence} /> : guide}
        stepKey="view"
        direction="back"
        testId="bridge-card"
        mode="chat"
        enter={!justCreated}
      >
        <ChatView
          view={view}
          bridgeKey={key}
          purpose={purpose}
          online={bridge.online}
          presence={bridge.presence}
          actions={bridge}
          share={share}
          onDelete={() => {
            setConfirmDelete(true);
          }}
          onRemoveMessage={(mid) => void removeMessage(mid)}
          notice={notice}
          backTo={backTo}
          copied={copied}
        />
      </AppCard>
    );
  }

  return (
    <Page compact>
      {body}
      <ConfirmDialog
        open={confirmDelete}
        title={t('bridgePage.confirmTitle')}
        text={t('bridgePage.confirmText')}
        confirmLabel={t('bridgePage.deleteBridge')}
        danger
        busy={deleting}
        onConfirm={() => void destroy()}
        onCancel={() => {
          setConfirmDelete(false);
        }}
      />
    </Page>
  );
}
